/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Elements to be rendered on Store tab in Dedicated Delivery page.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import AddIcon from "@mui/icons-material/Add";
import CachedIcon from "@mui/icons-material/Cached";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import agent from "../../../../app/api/agent";
import getAxiosError from "../../../../app/api/axiosError";
import {
  bulkStoreIngestion,
  getStoreData,
} from "../../../../app/features/dedicatedDelivery/storeSlice";
import { getfdpList } from "../../../../app/features/fdp/fdpSlice";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import { BulkStoreIngestionWrapper } from "../../../../app/models/responseModels/BulkStoreIngestionWrapper";
import { DdStorePerformanceResponse } from "../../../../app/models/responseModels/DdStorePerformanceResponse";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../../../components/common/filter/GridDataFilter";
import Loader from "../../../../components/common/loader/Loader";
import FileUploadModal from "../../../../components/common/modalPopup/FileUploadModal";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../../../components/common/modalPopup/useSubmitModal";
import { CustomTooltip } from "../../../../components/common/tooltip/CustomToolTip";
import { STATES_LIST } from "../../../fdp/FdpConstants";
import { prepareAndDownloadFile } from "../../../helper/helper";
import {
  ClientStoreProps,
  DD_STORE_COLUMN_NAME,
  DD_STORE_LABEL_NAME,
  ddStoreGridFilter,
} from "../../DdTypes";
import { getDefaultMRTOptions } from "../../DefaultMrtOptions";
import { ImportRouteData } from "../../ImportRouteData";
import AddStoreModal from "./AddStoreModal";
import ClientStoreActions from "./ClientStoreActions";
import { formatISODateString } from "../../../../components/common/utils/DateUtils";

function BulkStoreRequestModel(
  stores: any[],
  clientId: number,
  createdBy: string,
  updatedBy: string
): BulkStoreIngestionWrapper {
  return {
    stores: stores,
    clientId: clientId,
    createdBy: createdBy,
    updatedBy: updatedBy,
  };
}
const ClientStore = (props: ClientStoreProps) => {
  // access all parameters passed
  const clientId = props.clientId;
  const clientName = props.clientName;

  // import base configruation for grid
  const defaultMRTOptions = getDefaultMRTOptions<DdStorePerformanceResponse>();

  // import dispatcher from store
  const dispatch = useAppDispatch();

  // import store data from "Redux-Store"
  const { isLoading, storeData } = useSelector(
    (state: RootState) => state.dedicatedDeliveryStore
  );

  const [clonedStoreData, setClonedStoreData] =
    useState<DdStorePerformanceResponse[]>(storeData);

  // import fdp data from "Redux-Store"
  const { isFdpLoading, fdpList } = useSelector(
    (state: RootState) => state.fdp
  );

  const { authResponse } = useSelector((state: RootState) => state.login);

  // state flag to show loader on the screen when component is busy
  const [dataFetchig, setDataFetchig] = useState<boolean>(true);

  // state flag to show loader on the screen when component is busy
  const [exporting, setExporting] = useState<boolean>(false);

  // flag to open/close route per store modal
  const [openRoutesPerStoreModal, setOpenRoutesPerStoreModal] =
    useState<boolean>(false);

  // state to keep startDate
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject().setDate(props.startDate),
    new DateObject().setDate(props.endDate),
  ]);

  const [resetFilter, setResetFilter] = useState<boolean>(false);

  // set page title
  useEffect(() => {
    document.title = "Dedicated Delivery - Store";
  }, []);

  useEffect(() => {
    setClonedStoreData(storeData);
  }, [storeData]);

  // get overview data of store
  const getStoreOverviewData = useCallback(async () => {
    try {
      const response = await dispatch(
        getStoreData({
          clientId: clientId,
          storeIds: "",
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD"),
        })
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "storeData",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "storeDataException",
      });
    } finally {
      setDataFetchig(false);
      setResetFilter(false);
    }
  }, [dispatch, dateRange, clientId]);

  // get all available FDP(s)
  const getAllFdps = useCallback(async () => {
    try {
      const response = await dispatch(getfdpList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fdpData",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpDataException",
      });
    }
  }, [dispatch]);

  // call to db incase dependent fields are chagned
  useEffect(() => {
    if (dataFetchig) {
      getStoreOverviewData();
    }
  }, [dataFetchig, getStoreOverviewData]);

  // map columns to show on grid
  const columns = useMemo<MRT_ColumnDef<DdStorePerformanceResponse>[]>(
    () => [
      {
        accessorKey: DD_STORE_COLUMN_NAME.STOREID,
        header: DD_STORE_LABEL_NAME.STOREID,
        size: 100,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.CLIENTSTOREID,
        header: DD_STORE_LABEL_NAME.CLIENTSTOREID,
        size: 100,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.FDPS,
        header: DD_STORE_LABEL_NAME.FDPS,
        size: 100,
        Cell: ({ cell, row }) =>
          row.original.fdps.map((each) => each.companyName).join(", "),
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.LOCATION,
        header: DD_STORE_LABEL_NAME.LOCATION,
        size: 100,
        Cell: ({ cell, row }) => row.original.location.replaceAll("#", ","),
      },
      /* {
        accessorKey: DD_STORE_COLUMN_NAME.LATITUDE,
        header: DD_STORE_LABEL_NAME.LATITUDE,
        size: 100,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.LONGITUDE,
        header: DD_STORE_LABEL_NAME.LONGITUDE,
        size: 100,
      }, */
      {
        accessorKey: DD_STORE_COLUMN_NAME.GROSSPERFORMANCE,
        header: DD_STORE_LABEL_NAME.GROSSPERFORMANCE,
        size: 100,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.NETPERFORMANCE,
        header: DD_STORE_LABEL_NAME.NETPERFORMANCE,
        size: 100,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.STORECREATEDON,
        header: DD_STORE_LABEL_NAME.STORECREATEDON,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.STORECREATEDBY,
        header: DD_STORE_LABEL_NAME.STORECREATEDBY,
        size: 100,
      },
      {
        accessorKey: DD_STORE_COLUMN_NAME.STOREUPDATEDON,
        header: DD_STORE_LABEL_NAME.STOREUPDATEDON,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
    ],
    []
  );

  // open  modal logic  incase user selects any of the store
  const [addNewStoreModal, openNewStoreModal] = useSubmitModal(
    (closeNewStoreModal) => (
      <AddStoreModal
        clientIdArg={clientId}
        clientNameArg={clientName}
        closeAddStoreModal={closeNewStoreModal}
        onCompleted={getStoreOverviewData}
      />
    )
  );

  const handleBulkAddStore = async (
    fileMappedData: any[],
    invalidData: any[]
  ) => {
    const errorFileName = "Error_Bulk_Import_Store_";
    try {
      const invalidFileData = invalidData.map(({ data, error }) => ({
        "Nearest Airport Code": data.nearestAirportCode,
        "Client Store ID": data.clientStoreId,
        Address: data.address,
        City: data.city,
        State: data.state,
        Zipcode: data.zipcode,
        "Associated FDP ID(s)": data.associatedFdpIds,
        Error: error,
      }));
      if (fileMappedData.length === 0) {
        prepareAndDownloadFile(invalidFileData, errorFileName);
        return false;
      } else {
        const response = await dispatch(
          bulkStoreIngestion(
            BulkStoreRequestModel(
              fileMappedData,
              clientId,
              authResponse?.userSettings?.userName ?? "",
              authResponse?.userSettings?.userName ?? ""
            )
          )
        ).unwrap();
        if (response.isSuccess) {
          let data = response.data;
          if (data.length === 0 && invalidData.length === 0) {
            toast.success("File has been processed successfully.", {
              toastId: "AddStoreException",
            });
          } else {
            const transformData = data.map((eachData) => ({
              "Nearest Airport Code": eachData.nearestAirportCode,
              "Client Store ID": eachData.clientStoreId,
              Address: eachData.address,
              City: eachData.city,
              State: eachData.state,
              Zipcode: eachData.zipcode,
              "Associated FDP ID(s)": eachData.associatedFdpIds
                ? eachData.associatedFdpIds.toString()
                : "",
              Error: eachData.reason,
            }));
            // Combine invalidFileData and filteredResponseData
            const mergedData = [...invalidFileData, ...transformData];
            prepareAndDownloadFile(mergedData, errorFileName);
            toast.warning("Some Stores couldn't save, Please check the file!", {
              toastId: "AddBulkFdpException",
            });
          }
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "AddStoreException",
      });
    }
    return true;
  };

  // open  modal logic  incase user selects any of the store
  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        onCompleted={getStoreOverviewData}
        expectedColumns={[
          {
            name: "Nearest Airport Code",
            mandatory: true,
            type: "string",
            camelCaseName: "nearestAirportCode",
            regex: /^[A-Za-z0-9]{3}$/,
          },
          {
            name: "Client Store ID",
            mandatory: true,
            type: "string",
            camelCaseName: "clientStoreId",
            regex: /^[A-Za-z0-9]+$/,
          },
          {
            name: "Address",
            mandatory: true,
            type: "string",
            camelCaseName: "address",
            regex: /^(?!.*\s$)(?!^\s)[A-Za-z0-9\s.,-]+$/,
          },
          {
            name: "City",
            mandatory: true,
            type: "string",
            camelCaseName: "city",
            regex: /^(?=.*[^\s])[A-Za-z0-9 _-]+$/,
          },
          {
            name: "State",
            mandatory: true,
            type: "string",
            camelCaseName: "state",
            regex: new RegExp(`\\b(${STATES_LIST.join("|")})\\b`),
          },
          {
            name: "Zipcode",
            mandatory: true,
            type: "string",
            camelCaseName: "zipcode",
            regex: /^\d{5}(?:-\d{4})?$/,
          },
          {
            name: "Associated FDP ID(s)",
            mandatory: true,
            type: "number,[]",
            camelCaseName: "associatedFdpIds",
          },
        ]}
        asyncSubmitOperation={handleBulkAddStore}
        sampleFileName={"AddBulkStore.xlsx"}
      />
    )
  );

  const handleStoreExport = async (storeIds?: string | undefined) => {
    try {
      setExporting(true);
      if (dateRange) {
        const formatStartDateP = dateRange[0].format("YYYY-MM-DD");
        const formatEndDateP = dateRange[1].format("YYYY-MM-DD");
        // Send GET request to Spring Boot backend
        const response = await agent.DedicatedDeliveryAgent.exportStoreData({
          clientId: clientId,
          storeIds: storeIds ?? "",
          startDate: formatStartDateP,
          endDate: formatEndDateP,
        });

        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(response);
        downloadLink.download =
          clientName +
          "-Store-Report-" +
          formatStartDateP +
          "-to-" +
          formatEndDateP +
          ".xlsx";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Cleanup
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file:" + error);
      // Handle error
    } finally {
      setExporting(false);
    }
  };

  // elements to be rendered on top of grid
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<DdStorePerformanceResponse>
  ) => {
    return (
      <Box>
        <CustomTooltip arrow title="Refresh" onClick={getStoreOverviewData}>
          <IconButton type="button">
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          arrow
          title="Add New"
          onClick={() => {
            openNewStoreModal();
            getAllFdps();
          }}
        >
          <IconButton type="button">
            <AddIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>

        <CustomTooltip arrow title="Import" onClick={openFileUploadModal}>
          <IconButton type="button">
            <FileUploadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          arrow
          title="Export"
          onClick={() => {
            // if no selection made, then all rows based on date filter is exported.
            if (
              !table.getIsSomeRowsSelected() &&
              !table.getIsAllRowsSelected()
            ) {
              handleStoreExport(undefined);
            } else {
              // if selection made, then all selected rows are exported.
              handleStoreExport(
                table
                  .getSelectedRowModel()
                  .rows.map((row) => row.original.storeId)
                  .join(",")
              );
            }
          }}
        >
          <IconButton type="button">
            <FileDownloadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  // open  modal logic  incase user selects any of the store
  const [routesByStoreModal, openRoutesByStoreModal] =
    useSubmitModalWithParameter<MRT_Row<DdStorePerformanceResponse>>(
      (rowData, closeRoutesByStoreModal) => (
        <Modal
          aria-labelledby="new-store-add-modal"
          aria-describedby="new-store-add-modal-description"
          open={openRoutesPerStoreModal}
          onClose={() => {
            setOpenRoutesPerStoreModal(false);
            getStoreOverviewData();
          }}
          style={{ overflow: "scroll" }}
        >
          <Container
            component={Paper}
            style={{ marginTop: "2%", height: "auto" }}
          >
            <ClientStoreActions
              rowData={rowData}
              close={setOpenRoutesPerStoreModal}
              startDateP={dateRange[0]}
              endDateP={dateRange[1]}
              clientId={clientId}
              clientName={clientName}
              onCompleted={getStoreOverviewData}
            />
          </Container>
        </Modal>
      )
    );

  // store grid configuration
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: clonedStoreData,
    enableRowSelection: true,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {},
      sorting: [{ desc: true, id: "storeId" }],
    },
    state: {
      showProgressBars: isLoading,
    },
    getRowId: (row) => row.id.toString(),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        const clientStoreId = row.original.clientStoreId;
        openRoutesByStoreModal(row);
        setOpenRoutesPerStoreModal(true);
        getAllFdps();
        console.log(clientStoreId);
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
        bgcolor:
          +row.original.grossPerformance < 95
            ? "#E55451"
            : +row.original.grossPerformance > 97
            ? ""
            : "yellow",
        "&.MuiTableRow-root.Mui-selected td": {
          bgcolor: "#ccff90",
          color: "black",
        },
      },
    }),

    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {clonedStoreData.length}
        </Typography>
      );
    },
  });

  return (
    <>
      <Grid
        container
        paddingY={2}
        rowSpacing={2}
        justifyContent="space-between"
      >
        <Loader isLoading={exporting} />
        <Grid item>
          <Typography component="h2" variant="h3">
            Client: {clientName}
          </Typography>
        </Grid>
        <Grid item>
          <Grid container item alignItems="center">
            <Box zIndex={100} style={{ overflowY: "auto" }}>
              <DatePicker
                format="MM/DD/YYYY"
                maxDate={new Date()}
                className="green"
                dateSeparator=" ~ "
                range
                rangeHover
                value={dateRange}
                onChange={(dateObjects: DateObject[]) => {
                  setDateRange(dateObjects);
                }}
              />
            </Box>
            <Button
              size="large"
              onClick={() => {
                setDataFetchig(true);
                setResetFilter(true);
              }}
            >
              SUBMIT
            </Button>
          </Grid>
        </Grid>
        <Grid item>
          <ImportRouteData clientId={clientId} />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <GridDataFilter
          filterableColumns={ddStoreGridFilter}
          title="Search Store Data"
          dateRange={dateRange}
          resetFlag={resetFilter}
          asyncSubmitFunction={(filterState: FilterableColumnsState) => {
            if (Object.keys(filterState).length) {
              const filteredData = storeData.filter(
                (store: Record<string, any>) => {
                  for (const key in filterState) {
                    if (filterState.hasOwnProperty(key)) {
                      const filterValues = filterState[key].values;
                      if (key === "location") {
                        const storeLocation = store.location.replace(
                          /#/g,
                          ", "
                        );
                        if (!filterValues.includes(storeLocation)) {
                          return false;
                        }
                      } else if (key === "fdps") {
                        const companyNames = store.fdps.map(
                          (fdp: Record<string, any>) => fdp.companyName
                        );
                        if (
                          !companyNames.some((companyName: string) =>
                            filterValues.includes(companyName)
                          )
                        ) {
                          return false;
                        }
                      } else if (
                        (key === "storeCreatedOn" ||
                          key === "storeUpdatedOn") &&
                        filterValues.length === 2
                      ) {
                        const storeDate = new Date(store[key]);
                        const startDate = new Date(filterValues[0]);
                        const endDate = new Date(filterValues[1]);
                        endDate.setHours(23, 59, 59, 999);

                        const isDateInRange =
                          storeDate >= startDate && storeDate <= endDate;
                        if (!isDateInRange) {
                          return false;
                        }
                      } else {
                        if (!filterValues.includes(store[key])) {
                          return false;
                        }
                      }
                    }
                  }
                  return true;
                }
              );
              console.log(filterState, storeData, filteredData);
              setClonedStoreData(filteredData);
            } else {
              setClonedStoreData(storeData);
            }
          }}
          asyncSelectedColumnValues={(
            columnName: string,
            setClFilterableColumns: React.Dispatch<
              React.SetStateAction<FilterableColumns>
            >
          ) => {
            let filteredData: string[];
            if (columnName === "fdps") {
              filteredData = storeData
                .flatMap((e: Record<string, any>) => e[columnName])
                .map((e) => e.companyName)
                .filter((value) => value !== undefined && value !== null);
            } else if (columnName === "location") {
              filteredData = storeData
                .map((e: Record<string, any>) =>
                  e[columnName].replaceAll("#", ", ")
                )
                .filter((value) => value !== undefined && value !== null);
            } else {
              filteredData = storeData
                .map((e: Record<string, any>) => e[columnName])
                .filter((value) => value !== undefined && value !== null);
            }
            filteredData = Array.from(new Set(filteredData));
            setClFilterableColumns((prevState: FilterableColumns) => {
              return {
                ...prevState,
                [columnName]: {
                  ...prevState[columnName],
                  options: filteredData ?? [],
                },
              };
            });
          }}
        />
      </Grid>
      <Grid item xs={12} marginTop={2}>
        <MaterialReactTable table={table} />
      </Grid>
      {routesByStoreModal}
      {addNewStoreModal}
      {fileUploadModal}
    </>
  );
};
export default ClientStore;
